import React, { useState, useEffect } from 'react';
import { Search, Filter, Plus, Users, ChevronRight, X, Trash2, Edit3, AlertCircle, Check } from 'lucide-react';
import { userPatientAssignments } from '../services/api';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [assignments, setAssignments] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [eligiblePatients, setEligiblePatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Form states
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [assignmentsRes, usersRes, patientsRes] = await Promise.all([
        userPatientAssignments.getAllAssignments(),
        userPatientAssignments.getEligibleUsers(),
        userPatientAssignments.getEligiblePatients()
      ]);

      setAssignments(assignmentsRes.data);
      setEligibleUsers(usersRes.data);
      setEligiblePatients(patientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Falha ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Clear messages after a few seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Filter assignments based on search and role filter
  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = 
      assignment.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.patient?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.patient?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.patient?.patientId?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || assignment.user?.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  // --- Patient picker (Nova Associação modal) ---------------------------------
  // Patients already actively assigned to the chosen user: shown, but locked, so
  // "Selecionar todos" never tries to recreate an assignment the API would reject.
  const alreadyAssignedPatientIds = new Set(
    assignments
      .filter(a => (a.userId || a.user?.id) === selectedUser && a.status === 'active')
      .map(a => a.patientId || a.patient?.id)
  );

  const visiblePatients = eligiblePatients.filter(patient => {
    const query = patientSearch.trim().toLowerCase();
    if (!query) return true;
    return (
      patient.firstName?.toLowerCase().includes(query) ||
      patient.lastName?.toLowerCase().includes(query) ||
      patient.patientId?.toLowerCase().includes(query)
    );
  });

  const selectablePatients = visiblePatients.filter(p => !alreadyAssignedPatientIds.has(p.id));
  const allVisibleSelected =
    selectablePatients.length > 0 &&
    selectablePatients.every(p => selectedPatients.includes(p.id));

  const togglePatient = (patientId) => {
    setSelectedPatients(prev =>
      prev.includes(patientId)
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  // Select-all acts on what the search currently shows, not the whole list.
  const toggleSelectAllVisible = () => {
    const visibleIds = selectablePatients.map(p => p.id);
    setSelectedPatients(prev =>
      allVisibleSelected
        ? prev.filter(id => !visibleIds.includes(id))
        : [...new Set([...prev, ...visibleIds])]
    );
  };

  const resetAddForm = () => {
    setSelectedUser('');
    setSelectedPatients([]);
    setPatientSearch('');
    setNotes('');
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    resetAddForm();
  };

  // Handle form submission for adding assignments (one or many patients at once)
  const handleAddAssignment = async (e) => {
    e.preventDefault();

    if (!selectedUser || selectedPatients.length === 0) {
      setError('Por favor, selecione o usuário e ao menos um paciente');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // The API creates one assignment per call, so send them in small batches:
      // "Selecionar todos" over a large patient list should not fire hundreds of
      // simultaneous requests at the backend.
      const BATCH_SIZE = 4;
      const trimmedNotes = notes.trim() || null;
      let created = 0;
      const failedIds = [];
      const failedNames = [];

      for (let i = 0; i < selectedPatients.length; i += BATCH_SIZE) {
        const batch = selectedPatients.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(
          batch.map(patientId =>
            userPatientAssignments.createAssignment({
              userId: selectedUser,
              patientId,
              notes: trimmedNotes
            })
          )
        );

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            created += 1;
            return;
          }
          const patientId = batch[index];
          const patient = eligiblePatients.find(p => p.id === patientId);
          failedIds.push(patientId);
          failedNames.push(patient ? `${patient.firstName} ${patient.lastName}` : patientId);
          console.error('Error creating assignment:', result.reason);
        });
      }

      await fetchData();

      if (failedIds.length === 0) {
        setSuccess(
          created === 1
            ? 'Associação criada com sucesso'
            : `${created} associações criadas com sucesso`
        );
        closeAddModal();
      } else {
        // Keep the modal open with only the failures still selected, so the admin
        // can see what did not go through and retry just those.
        setSelectedPatients(failedIds);
        const shown = failedNames.slice(0, 3).join(', ');
        const extra = failedNames.length > 3 ? ` e mais ${failedNames.length - 3}` : '';
        setError(`${created} associação(ões) criada(s). Falha em ${failedIds.length}: ${shown}${extra}`);
      }
    } catch (error) {
      console.error('Error creating assignments:', error);
      setError(error.response?.data?.error || 'Falha ao criar associação');
    } finally {
      setLoading(false);
    }
  };

  // Handle assignment update
  const handleUpdateAssignment = async (e) => {
    e.preventDefault();
    
    if (!selectedAssignment) return;

    try {
      setLoading(true);
      await userPatientAssignments.updateAssignment(selectedAssignment.id, {
        notes: notes.trim() || null
      });

      setSuccess('Associação atualizada com sucesso');
      setShowEditModal(false);
      setSelectedAssignment(null);
      setNotes('');
      await fetchData();
    } catch (error) {
      console.error('Error updating assignment:', error);
      setError(error.response?.data?.error || 'Falha ao atualizar associação');
    } finally {
      setLoading(false);
    }
  };

  // Handle assignment deletion
  const handleDeleteAssignment = async () => {
    if (!selectedAssignment) return;

    try {
      setLoading(true);
      await userPatientAssignments.deleteAssignment(selectedAssignment.id);
      
      setSuccess('Associação removida com sucesso');
      setShowDeleteConfirm(false);
      setSelectedAssignment(null);
      await fetchData();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      setError(error.response?.data?.error || 'Falha ao remover associação');
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (assignment) => {
    setSelectedAssignment(assignment);
    setNotes(assignment.notes || '');
    setShowEditModal(true);
  };

  // Open delete confirmation
  const openDeleteConfirm = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDeleteConfirm(true);
  };

  if (loading && assignments.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        <span className="ml-2 text-gray-600">Carregando...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Atribuir Pacientes</h1>
          <p className="text-gray-600">Defina quais pacientes cada supervisor ou terapeuta pode acessar</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 bg-brand-700 text-white rounded-lg hover:bg-brand-800 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Associação
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center p-4 text-red-800 border border-red-300 bg-red-50 rounded-lg">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center p-4 text-green-800 border border-green-300 bg-green-50 rounded-lg">
          <Check className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por usuário ou paciente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
          >
            <option value="all">Todas as funções</option>
            <option value="supervisor">Supervisores</option>
            <option value="therapist">Terapeutas</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-brand-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total de Associações</p>
              <p className="text-2xl font-bold text-gray-900">{assignments.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Supervisores</p>
              <p className="text-2xl font-bold text-gray-900">
                {assignments.filter(a => a.user?.role === 'supervisor').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Terapeutas</p>
              <p className="text-2xl font-bold text-gray-900">
                {assignments.filter(a => a.user?.role === 'therapist').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {filteredAssignments.length === 0 ? (
          <div className="p-6 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">Nenhuma associação encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">
              {assignments.length === 0 
                ? 'Comece criando uma nova associação entre usuários e pacientes.'
                : 'Tente ajustar os filtros de busca.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paciente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Associado por
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Observações
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAssignments.map((assignment) => (
                  <tr key={assignment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-brand-600">
                              {assignment.user?.firstName?.charAt(0)}{assignment.user?.lastName?.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {assignment.user?.firstName} {assignment.user?.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {assignment.user?.email}
                          </div>
                          <div className="text-xs text-gray-400 capitalize">
                            {assignment.user?.role}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {assignment.patient?.firstName} {assignment.patient?.lastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        ID: {assignment.patient?.patientId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {assignment.assignedByUser?.firstName} {assignment.assignedByUser?.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(assignment.assignedAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {assignment.notes || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(assignment)}
                          className="text-brand-600 hover:text-brand-900"
                          title="Editar"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(assignment)}
                          className="text-red-600 hover:text-red-900"
                          title="Remover"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Assignment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Nova Associação</h3>
              <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddAssignment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Usuário (Supervisor ou Terapeuta)
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => {
                    setSelectedUser(e.target.value);
                    setSelectedPatients([]);
                  }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  required
                >
                  <option value="">Selecione um usuário</option>
                  {eligibleUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Pacientes
                  </label>
                  <span className="text-xs text-gray-500">
                    {selectedPatients.length} selecionado(s)
                  </span>
                </div>

                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    placeholder="Buscar paciente por nome ou ID..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                  {!selectedUser ? (
                    <p className="p-3 text-sm text-gray-500">
                      Selecione um usuário para listar os pacientes.
                    </p>
                  ) : visiblePatients.length === 0 ? (
                    <p className="p-3 text-sm text-gray-500">Nenhum paciente encontrado.</p>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      <label className="flex items-center gap-3 p-3 bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          disabled={selectablePatients.length === 0}
                          className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {allVisibleSelected ? 'Limpar seleção' : 'Selecionar todos'}
                          {patientSearch.trim() ? ' (resultados da busca)' : ''}
                        </span>
                        <span className="ml-auto text-xs text-gray-500">
                          {selectablePatients.length} disponível(is)
                        </span>
                      </label>

                      {visiblePatients.map(patient => {
                        const isAssigned = alreadyAssignedPatientIds.has(patient.id);
                        return (
                          <label
                            key={patient.id}
                            className={`flex items-center gap-3 p-3 ${
                              isAssigned
                                ? 'opacity-60 cursor-not-allowed'
                                : 'cursor-pointer hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isAssigned || selectedPatients.includes(patient.id)}
                              onChange={() => togglePatient(patient.id)}
                              disabled={isAssigned}
                              className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                            />
                            <span className="text-sm text-gray-900">
                              {patient.firstName} {patient.lastName}
                              <span className="text-gray-500"> (ID: {patient.patientId})</span>
                            </span>
                            {isAssigned && (
                              <span className="ml-auto text-xs text-gray-500 whitespace-nowrap">
                                já associado
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações (opcional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="Informações adicionais sobre a associação..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !selectedUser || selectedPatients.length === 0}
                  className="flex-1 px-4 py-2 bg-brand-700 text-white rounded-lg hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading
                    ? 'Criando...'
                    : selectedPatients.length > 1
                      ? `Criar ${selectedPatients.length} Associações`
                      : 'Criar Associação'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Assignment Modal */}
      {showEditModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Editar Associação</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Usuário:</strong> {selectedAssignment.user?.firstName} {selectedAssignment.user?.lastName}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Paciente:</strong> {selectedAssignment.patient?.firstName} {selectedAssignment.patient?.lastName}
              </p>
            </div>

            <form onSubmit={handleUpdateAssignment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="Informações adicionais sobre a associação..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-brand-700 text-white rounded-lg hover:bg-brand-800 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Confirmar Remoção</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Deseja realmente remover a associação entre <strong>{selectedAssignment.user?.firstName} {selectedAssignment.user?.lastName}</strong> e o paciente <strong>{selectedAssignment.patient?.firstName} {selectedAssignment.patient?.lastName}</strong>?
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteAssignment}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Removendo...' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;